import { useState } from "react";
import { useVideosByIds } from "@/api/video";
import { VideoEmbed } from "@/pages/videos/video-embed";
import { formatChartValue, type ResolvedVisualization } from "../lib/charts";
import { isCreatorColumn, LINK_STYLE, normaliseHandle, videoUrl } from "../lib/tiktok-links";
import Icon from "./Icon";

const TWO_LINES: React.CSSProperties = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  overflowWrap: "anywhere",
};

const ONE_LINE: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
};

type Preview = {
  id: string;
  handle: string;
  line: string;
  value: unknown;
};

function VideoCard({
  preview,
  viz,
  info,
}: {
  preview: Preview;
  viz: ResolvedVisualization;
  info?: { videoTitle: string; thumbnail: string | null; videoUrl: string | null; creatorName: string | null };
}) {
  const [playing, setPlaying] = useState(false);
  const handle = preview.handle || normaliseHandle(info?.creatorName ?? "");
  const url = info?.videoUrl ?? videoUrl(preview.id, handle || undefined);
  const title = preview.line || info?.videoTitle || "";
  const value = viz.valueColumns[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      {playing ? (
        <div style={{ width: "100%", aspectRatio: "9 / 16", borderRadius: 12, overflow: "hidden" }}>
          <VideoEmbed url={url} videoId={preview.id} thumbnail={info?.thumbnail} width="100%" noScroll />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          title={title || "Play video"}
          aria-label={`Play video${handle ? ` by ${handle}` : ""}`}
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "9 / 14",
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid var(--color-border-secondary)",
            background: "var(--color-bg-tertiary)",
            padding: 0,
            cursor: "pointer",
          }}
        >
          {info?.thumbnail && (
            <img
              src={info.thumbnail}
              alt=""
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.45) 100%)",
            }}
          />
          {title && (
            <div
              style={{
                ...TWO_LINES,
                position: "absolute",
                top: 10,
                left: 10,
                right: 10,
                color: "#fff",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {title}
            </div>
          )}
          <span
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="play" size={18} color="currentColor" strokeWidth={2} />
          </span>
          {value && preview.value != null && (
            <span
              style={{
                position: "absolute",
                left: 8,
                bottom: 8,
                color: "#fff",
                fontSize: 11,
                background: "rgba(0,0,0,0.5)",
                padding: "1px 6px",
                borderRadius: 4,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatChartValue(preview.value, viz.valueFormat, viz.unit, true)}
            </span>
          )}
        </button>
      )}
      {handle && (
        <a
          href={`https://www.tiktok.com/@${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          title={handle}
          style={{ ...LINK_STYLE, ...ONE_LINE, fontSize: 13 }}
        >
          {handle}
        </a>
      )}
    </div>
  );
}

export default function VideoPreviews({ viz }: { viz: ResolvedVisualization }) {
  const idCol = viz.categoryColumn;
  const handleCol = viz.columns.find(isCreatorColumn);
  const previews: Preview[] = idCol
    ? viz.rows
        .map((r) => ({
          id: String(r[idCol] ?? "").trim(),
          handle: handleCol ? normaliseHandle(String(r[handleCol] ?? "")) : "",
          line: viz.detailColumn ? String(r[viz.detailColumn] ?? "").trim() : "",
          value: viz.valueColumns[0] ? r[viz.valueColumns[0]] : null,
        }))
        .filter((p) => /^\d{6,}$/.test(p.id))
    : [];
  const { data } = useVideosByIds(previews.map((p) => p.id));

  if (previews.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: 12,
      }}
    >
      {previews.map((p) => (
        <VideoCard key={p.id} preview={p} viz={viz} info={data?.[p.id]} />
      ))}
    </div>
  );
}
